import { useEffect, useRef } from "react";
import type { KeyboardEvent, MouseEvent, PointerEvent } from "react";

const KEYS = [
  { semitone: 0, label: "C", key: "A", black: false },
  { semitone: 1, label: "C♯", key: "W", black: true },
  { semitone: 2, label: "D", key: "S", black: false },
  { semitone: 3, label: "D♯", key: "E", black: true },
  { semitone: 4, label: "E", key: "D", black: false },
  { semitone: 5, label: "F", key: "F", black: false },
  { semitone: 6, label: "F♯", key: "T", black: true },
  { semitone: 7, label: "G", key: "G", black: false },
  { semitone: 8, label: "G♯", key: "Y", black: true },
  { semitone: 9, label: "A", key: "H", black: false },
  { semitone: 10, label: "A♯", key: "U", black: true },
  { semitone: 11, label: "B", key: "J", black: false },
  { semitone: 12, label: "C", key: "K", black: false },
] as const;

export function Piano({ octave, activeNotes, onNoteOn, onNoteOff }: {
  octave: number;
  activeNotes: number[];
  onNoteOn: (owner: string, note: number, velocity: number) => void;
  onNoteOff: (owner: string) => void;
}) {
  const base = 12 * (octave + 1);
  const activePointerOwners = useRef(new Set<string>());
  const heldKeyboardOwners = useRef(new Set<string>());
  const suppressedClicks = useRef(new Set<string>());
  const activationTimers = useRef(new Map<string, number>());

  useEffect(() => () => {
    for (const owner of activePointerOwners.current) onNoteOff(owner);
    for (const owner of heldKeyboardOwners.current) onNoteOff(owner);
    for (const [owner, timer] of activationTimers.current) {
      window.clearTimeout(timer);
      onNoteOff(owner);
    }
    activePointerOwners.current.clear();
    heldKeyboardOwners.current.clear();
    activationTimers.current.clear();
  }, [base, onNoteOff]);

  const pointerOwner = (pointerId: number, note: number) => `pointer-${pointerId}-${note}`;
  const keyboardOwner = (note: number) => `piano-key-${note}`;

  const pointerDown = (event: PointerEvent<HTMLButtonElement>, note: number) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const bounds = event.currentTarget.getBoundingClientRect();
    const velocity = Math.min(1, Math.max(0.2, (event.clientY - bounds.top) / bounds.height));
    const owner = pointerOwner(event.pointerId, note);
    activePointerOwners.current.add(owner);
    onNoteOn(owner, note, velocity);
  };

  const pointerUp = (event: PointerEvent<HTMLButtonElement>, note: number) => {
    const owner = pointerOwner(event.pointerId, note);
    if (activePointerOwners.current.delete(owner)) onNoteOff(owner);
  };

  const keyDown = (event: KeyboardEvent<HTMLButtonElement>, note: number) => {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    const owner = keyboardOwner(note);
    if (event.repeat || heldKeyboardOwners.current.has(owner)) return;
    heldKeyboardOwners.current.add(owner);
    onNoteOn(owner, note, 0.82);
  };

  const keyUp = (event: KeyboardEvent<HTMLButtonElement>, note: number) => {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    const owner = keyboardOwner(note);
    if (!heldKeyboardOwners.current.delete(owner)) return;
    onNoteOff(owner);
    // Space normally synthesizes a click after keyup. Keep that click from
    // retriggering the short assistive-technology audition below.
    suppressedClicks.current.add(owner);
    window.setTimeout(() => suppressedClicks.current.delete(owner), 0);
  };

  const blur = (note: number) => {
    const owner = keyboardOwner(note);
    if (heldKeyboardOwners.current.delete(owner)) onNoteOff(owner);
  };

  const activate = (event: MouseEvent<HTMLButtonElement>, note: number) => {
    // Pointer gestures already have velocity-sensitive down/up handling.
    if (event.detail > 0) return;
    const keyOwner = keyboardOwner(note);
    if (heldKeyboardOwners.current.has(keyOwner) || suppressedClicks.current.delete(keyOwner)) return;

    // Screen readers and voice-control software commonly dispatch click
    // without pointer or key events. Give that activation an audible note.
    const owner = `piano-activation-${note}`;
    const previous = activationTimers.current.get(owner);
    if (previous !== undefined) window.clearTimeout(previous);
    onNoteOn(owner, note, 0.82);
    activationTimers.current.set(owner, window.setTimeout(() => {
      activationTimers.current.delete(owner);
      onNoteOff(owner);
    }, 180));
  };
  return (
    <section className="piano-shell" aria-label="Four voice piano keyboard">
      <div className="piano-heading"><span>PERFORMANCE KEYBED</span><small>QWERTY · Z/X OCTAVE</small></div>
      <div className="piano">
        {KEYS.map(({ semitone, label, key, black }) => {
          const note = base + semitone;
          return (
            <button
              type="button"
              key={`${label}-${note}`}
              className={`piano-key ${black ? "piano-key--black" : "piano-key--white"}`}
              aria-label={`${label}${octave + (semitone === 12 ? 1 : 0)}, keyboard ${key}`}
              aria-keyshortcuts={key}
              aria-pressed={activeNotes.includes(note)}
              onPointerDown={(event) => pointerDown(event, note)}
              onPointerUp={(event) => pointerUp(event, note)}
              onPointerCancel={(event) => pointerUp(event, note)}
              onLostPointerCapture={(event) => pointerUp(event, note)}
              onKeyDown={(event) => keyDown(event, note)}
              onKeyUp={(event) => keyUp(event, note)}
              onBlur={() => blur(note)}
              onClick={(event) => activate(event, note)}
            >
              <span>{key}</span><small>{label}</small>
            </button>
          );
        })}
      </div>
    </section>
  );
}
